import { useQuery } from "@tanstack/react-query";

import type {
    AdIntelligenceAdvertiserCampaignsDataListParams,
    AdIntelligencePublisherVerificationPublishersRequest,
    AdIntelligenceRecordsPublisherVerificationPublishersResponse,
} from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client/apiClient";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";

interface IUseVerificationFilterOptionsArgs {
    advertiser: string;
}

interface IUseVerificationCampaignsOptionsArgs extends IUseVerificationFilterOptionsArgs {
    search?: string;
}

const CAMPAIGNS_PAGE_SIZE = 100;

const useDurationAndCountry = () => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const durationData = DurationService.getDurationData(params.duration ?? "");
    const { from, to } = durationData.forAPI.iso;
    return { country: params.country, from, to };
};

export const useVerificationPublishersOptions = ({ advertiser }: IUseVerificationFilterOptionsArgs) => {
    const { country, from, to } = useDurationAndCountry();

    const body: AdIntelligencePublisherVerificationPublishersRequest = {
        advertiser,
        country,
        dateFrom: from,
        dateTo: to,
    };

    return useQuery<AdIntelligenceRecordsPublisherVerificationPublishersResponse>({
        queryKey: ["WebsiteDisplayAds", "verification", "publishers-filter", advertiser, country, from, to],
        queryFn: () =>
            apiClient.AdIntelligenceMediaPartners.adIntelligenceAdvertiserPublisherVerificationPublishersCreate(body),
        enabled: Boolean(advertiser && country && from && to),
    });
};

export const useVerificationCampaignsOptions = ({ advertiser, search }: IUseVerificationCampaignsOptionsArgs) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const country = params.country;
    // This endpoint validates against the latest supported month, so use the apiFormat range plus
    // isWindow/latest (like the campaigns table) instead of the literal iso dates — otherwise a
    // window duration sends an out-of-range `to` and the BE rejects the interval.
    const { from, to, isWindow } = DurationService.getDurationData(params.duration ?? "").forAPI;
    const includeTerms = search?.trim() || undefined;

    const body: AdIntelligenceAdvertiserCampaignsDataListParams = {
        key: [advertiser],
        country,
        from,
        to,
        isWindow,
        ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        includeTerms,
        pageSize: CAMPAIGNS_PAGE_SIZE,
    };

    return useQuery({
        queryKey: [
            "WebsiteDisplayAds",
            "verification",
            "campaigns-filter",
            advertiser,
            country,
            from,
            to,
            isWindow,
            includeTerms,
        ],
        queryFn: () => apiClient.AdIntelligenceMarketingAssets.adIntelligenceAdvertiserCampaignsDataList(body),
        enabled: Boolean(advertiser && country && from && to),
    });
};
